<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';
	import {
		ControlsContainer,
		DeviceButton,
		Text,
		TemperatureWheel,
		Slider,
		IconButton,
		Alert,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import {
		celsius_to_fahrenheit,
		defaultTempFavPositions,
		fahrenheit_to_celsius,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	import BluTrvBluetoothDisableAlert from './BluTrvBluetoothDisableAlert.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { BluTRVInterfaceData } from 'src/types/TRVComponent';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';

	$: settingsStore = getDeviceSettings(deviceID);
	export let device: Device, statuses: BluTRVInterfaceData[];
	/** When true, the temp favorite positions alert is not rendered (e.g. when shown in parent). */
	export let hideTempFavPositionsAlert = false;

	const unit = device?.ht_ui?.temperature_unit || 'C';
	const min = unit === 'C' ? 4 : Math.round(celsius_to_fahrenheit(4));
	const max = unit === 'C' ? 30 : Math.round(celsius_to_fahrenheit(30));
	const btnStep = 0.1;
	const step = unit === 'C' ? btnStep : 1;
	const deviceID = real_id(device.id);
	let temp_fav_positions = device?.temp_fav_positions || defaultTempFavPositions(unit);

	// Valve position favorites for manual mode
	$: valve_fav_positions = $devices[device.id]?.valve_fav_positions || [];
	$: mappedValveMarks = valve_fav_positions.map((val) => ({
		value: val,
		ariaLabel: $format('label_favorite_position_value', {
			values: { value: val },
			default: 'Favorite position {value}%',
		}),
	}));

	let waitForTargetTempResponse = false;
	let waitForBoostResponse = false;
	let sendTimeout: NodeJS.Timeout;
	let targetLock = false;
	let targetValue: number = null;

	const onChange = async (e: any) => {
		targetLock = true;
		targetValue = e.detail.value;
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(sendChange, 1000, e.detail.value);
	};

	const sendChange = async (value: number) => {
		let convertedValue = unit === 'C' ? value : +fahrenheit_to_celsius(value).toFixed(1);
		waitForTargetTempResponse = true;
		let res: SettingRequest;
		if (enableTrv) {
			res = await callBluTRV(device, 'Trv.SetTarget', {
				id: 0,
				target_C: convertedValue,
			});
		} else {
			res = await callBluTRV(device, 'Trv.SetPosition', {
				id: 0,
				pos: convertedValue,
			});
		}
		if (!res.isok) {
			targetLock = false;
			onChangeTargetValue(targetStatus?.event);
		}
		waitForTargetTempResponse = false;
	};

	const setBoost = async () => {
		waitForBoostResponse = true;
		await callBluTRV(device, 'Trv.SetBoost', { id: 0 });
		waitForBoostResponse = false;
	};

	const clearBoost = async () => {
		waitForBoostResponse = true;
		await callBluTRV(device, 'Trv.ClearBoost', { id: 0 });
		waitForBoostResponse = false;
	};

	const onChangeTargetValue = (value: number) => {
		if (targetValue == value) targetLock = false;
		if (value != null && !targetLock && targetValue != value) {
			targetValue = value;
		}
	};

	// Valve position favorites handlers
	const handleAddValveMark = async (e: CustomEvent) => {
		const newPosition = e.detail.value;
		const currentFavorites = $devices[device.id]?.valve_fav_positions || [];

		// Don't add if already exists or limit reached
		if (currentFavorites.includes(newPosition) || currentFavorites.length >= 4) {
			return;
		}

		await requestEditDevice({
			...$devices[device.id],
			valve_fav_positions: [...currentFavorites, newPosition],
		});
	};

	const handleDeleteValveMark = async (e: CustomEvent) => {
		const positionToDelete = e.detail.index;
		const currentFavorites = $devices[device.id]?.valve_fav_positions || [];

		const newFavorites = [...currentFavorites];
		newFavorites.splice(positionToDelete, 1);

		await requestEditDevice({
			...$devices[device.id],
			valve_fav_positions: newFavorites,
		});
	};

	// Quick valve control handlers
	const setValvePosition = async (position: number) => {
		// Optimistic UI update - immediately show the position change
		targetLock = true;
		targetValue = position;
		waitForTargetTempResponse = true;
		const res: SettingRequest = await callBluTRV(device, 'Trv.SetPosition', {
			id: 0,
			pos: position,
		});
		if (!res.isok) {
			targetLock = false;
			// Revert to actual position on error
			onChangeTargetValue(targetStatus?.event);
		}
		waitForTargetTempResponse = false;
	};

	$: boost = statuses.find((i) => i.name == 'boost time');
	$: current = statuses.find((el) => el.name === 'current_temperature');
	$: targetStatus = statuses.find(
		(i) => i.name == (enableTrv ? 'target_temperature' : 'valve position'),
	);
	$: enableTrv = getTrvComponentByDevice(device)?.config?.['trv:0']?.enable;
	$: onChangeTargetValue(targetStatus?.event);

	const a11yProps = {
		ariaLabel: enableTrv
			? $format('label_set_target_temperature', {
					default: 'Set target temperature',
				})
			: $format('label_set_valve_position', { default: 'Set valve position' }),
		ariaLabelValue: targetValue ? `${targetValue} ${targetStatus?.unit}` : null,
		'aria-valuemin': min,
		'aria-valuemax': max,
		'aria-valuenow': targetValue,
		'aria-valuetext': targetValue ? `${targetValue} ${targetStatus?.unit}` : null,
	};

	const a11yPropsTempWheel = {
		tempWheelAriaLabel: $format('label_temperature', {
			default: 'Temperature',
		}),
		thumbAriaLabel: $format('label_temperature_thumb', {
			default: 'Temperature thumb',
		}),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
		spinnerAriaLabel: $format('label_temperature_spinner', {
			default: 'Temperature spinner',
		}),
	};
</script>

{#if $settingsStore?.ble?.enable === false}
	<BluTrvBluetoothDisableAlert {deviceID} />
{/if}

{#if targetStatus}
	<div class="flex-full-height justify-content-end">
		{#if enableTrv}
			<TemperatureWheel
				class="mb-m text-center"
				a11yProps={a11yPropsTempWheel}
				valuePrefix={targetStatus.unit}
				value={targetValue}
				current={{
					value: current.value,
					description: $format('label_current_temperature', {
						default: 'Current temperature',
					}),
				}}
				{min}
				{max}
				{step}
				{btnStep}
				type={'temp'}
				icon={'fas fa-temperature-three-quarters'}
				isLoading={waitForTargetTempResponse}
				on:value_change={onChange}
			/>
			<div class="d-flex justify-content-center mb-s">
				{#each temp_fav_positions as pos, i}
					<IconButton
						id={`temp_fav_position_${pos.mode}_${i}`}
						aria-labelledby={`temp_fav_position_${pos.mode}_${i}`}
						title={$format(`temp_fav_position_${pos.mode}`, {
							default: capFirstChar(pos.mode),
						})}
						class="ml-xs mr-xs"
						disabled={!pos?.active}
						icon={getWorkingModeIcon(pos.mode)}
						variant={`${pos?.active && +targetValue === +pos.value ? 'primary' : 'secondary'}`}
						on:click={() => onChange({ detail: { value: +pos.value } })}
					/>
				{/each}
			</div>
			{#if !hideTempFavPositionsAlert && !device?.shared && temp_fav_positions?.filter((pos) => pos.active).length == 0}
				<Alert
					type="info"
					heading={$format('alert_info_heading_temp_fav_positions', {
						default:
							'You can enable or disable temperature favorite positions from sensor settings',
					})}
					cta={{
						title: $format('button_enable_disable', {
							default: 'Enable/Disable',
						}),
					}}
					on:cta_click={() => {
						navigateToSetting(
							device.id,
							'sensor_settings',
							'temperature_favorite_positions',
						);
					}}
				/>
			{/if}
		{:else}
			<Slider
				id="slider-s6gv1bl"
				{a11yProps}
				class="flex-full-height mb-s"
				title={`${capFirstChar($format('label_valve_position', { default: 'valve position' }))}:`}
				unit={targetStatus.unit}
				isLoading={waitForTargetTempResponse}
				value={targetValue}
				marks={mappedValveMarks}
				haveMarks={true}
				stopSaveDeleteMarks={device.shared}
				on:value_change={onChange}
				on:new_mark={!device.shared && ((e) => handleAddValveMark(e))}
				on:delete_mark={!device.shared && ((e) => handleDeleteValveMark(e))}
			/>
			<ControlsContainer class="mt-s" color={$showNewLayout ? 'color-0' : undefined}>
				<DeviceButton
					slot="first"
					id="devicebutton-close-valve"
					aria-label={$format('button_close_valve', { default: 'Close valve' })}
					variant="danger"
					size="m"
					icon="fas fa-circle-xmark"
					disabled={waitForTargetTempResponse}
					isLoading={waitForTargetTempResponse && targetValue === 0}
					on:click={() => setValvePosition(0)}
				/>
				<DeviceButton
					slot="second"
					id="devicebutton-open-valve"
					aria-label={$format('button_open_valve', { default: 'Open valve' })}
					variant="success"
					size="m"
					icon="fas fa-circle-check"
					disabled={waitForTargetTempResponse}
					isLoading={waitForTargetTempResponse && targetValue === 100}
					on:click={() => setValvePosition(100)}
				/>
			</ControlsContainer>
		{/if}
		{#if enableTrv}
			<ControlsContainer class="mt-s" color={$showNewLayout ? 'color-0' : undefined}>
				<svelte:fragment slot="first">
					<DeviceButton
						id="devicebutton-z8g69t7"
						aria-label={boost
							? $format('label_cancel_boost', { default: 'Cancel Boost' })
							: $format('label_start_boost', { default: 'Start Boost' })}
						icon="far fa-fire"
						variant={boost != null ? 'danger' : 'primary'}
						isActive={boost != null}
						isLoading={waitForBoostResponse}
						disabled={waitForBoostResponse}
						isWide
						size="l"
						on:click={(e) => {
							e.stopPropagation();
							if (boost) {
								clearBoost();
							} else {
								setBoost();
							}
						}}
					/>
					<Text class="text-bold mt-s"
						>{$format('sub_settings_title_boost', { default: 'Boost' })}</Text
					>
				</svelte:fragment>
			</ControlsContainer>
		{/if}
	</div>
{:else if statuses?.length == 0}
	<Text class="mb-s text-bold text-center text-color-3  body-small-styles"
		>{$format('text_blu_trv_fetching_data', {
			default: 'Fetching data...',
		})}
	</Text>
{/if}
